import { Box, makeStyles, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { Data as PopperData } from "popper.js";
import React from "react";
import Joyride, { ACTIONS, EVENTS, STATUS, Step, TooltipRenderProps } from "react-joyride";

import { CustomButton } from "@/components/Button";
import { layoutTokens, semanticColors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

const useStyles = makeStyles({
  tooltip: {
    width: 294,
    maxWidth: "calc(100vw - 32px)",
    padding: layoutTokens.space[16],
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[12],
    backgroundColor: semanticColors["surface/card"],
    borderRadius: layoutTokens.radius.md,
    boxShadow: `0 2px 8px ${alpha(semanticColors["text/primary"], 0.08)}`,
    [theme.breakpoints.up("md")]: { width: 340 },
  },
  content: { ...textStyles["SP/Body/Large"], color: semanticColors["text/primary"], whiteSpace: "pre-line", [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"] },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: layoutTokens.space[12] },
  progress: { ...textStyles["Number/Caption"], color: semanticColors["text/secondary"], whiteSpace: "nowrap" },
  actions: { display: "flex", alignItems: "center", gap: layoutTokens.space[8], "& > div": { width: "auto" } },
});

export interface GuidedTourProps {
  run: boolean;
  steps: Step[];
  stepIndex: number;
  onNext: () => void;
  onComplete: () => void;
  disableCloseOnEsc?: boolean;
}

export const GuidedTour: React.FC<GuidedTourProps> = ({ run, steps, stepIndex, onNext, onComplete, disableCloseOnEsc = false }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const viewportBoundary = React.useRef<HTMLDivElement>(null);
  const currentTarget = steps[stepIndex]?.target;
  React.useLayoutEffect(() => {
    if (!run || !currentTarget) return;
    const target = typeof currentTarget === "string" ? document.querySelector<HTMLElement>(currentTarget) : currentTarget;
    if (!target) return;
    const previousBackground = target.style.backgroundColor;
    const previousFlex = target.style.flex;
    target.style.backgroundColor = semanticColors["surface/card"];
    // 上部navの余白は設定ボタンのauto marginが吸収し、案内中だけ5つのメニューを囲む。
    if (target.tagName === "NAV") target.style.flex = "0 1 auto";
    return () => { target.style.backgroundColor = previousBackground; target.style.flex = previousFlex; };
  }, [run, currentTarget]);
  const Tooltip = ({ step, tooltipProps }: TooltipRenderProps) => (
    <Box {...tooltipProps} className={classes.tooltip} aria-label="はじめての案内">
      <Typography component="div" className={classes.content}>{step.content}</Typography>
      <Box className={classes.footer}>
        <Typography className={classes.progress} aria-label={`案内 ${stepIndex + 1} / ${steps.length}`}>
          {stepIndex + 1} / {steps.length}
        </Typography>
        <Box className={classes.actions}>
          <CustomButton tone="text" buttonSize="S" fullWidth={false} onClick={onComplete}>スキップ</CustomButton>
          <CustomButton buttonSize="S" fullWidth={false} onClick={stepIndex === steps.length - 1 ? onComplete : onNext}>
            {stepIndex === steps.length - 1 ? "はじめる" : "次へ"}
          </CustomButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <div ref={viewportBoundary} aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", visibility: "hidden" }} />
      <Joyride
        run={run}
        steps={steps.map(step => ({ ...step, disableBeacon: true, offset: layoutTokens.space[12] }))}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep={false}
        scrollOffset={220}
        spotlightPadding={layoutTokens.space[8]}
        spotlightClicks={false}
        disableOverlayClose
        disableCloseOnEsc={disableCloseOnEsc}
        hideCloseButton
        tooltipComponent={Tooltip}
        callback={data => {
          if (data.type === EVENTS.TARGET_NOT_FOUND) onNext();
          if (data.action === ACTIONS.CLOSE || data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) onComplete();
        }}
        styles={{
          options: { zIndex: theme.zIndex.modal + 1, overlayColor: semanticColors["surface/overlay"], arrowColor: semanticColors["surface/card"], width: isMobile ? 294 : 340 },
          overlay: { backgroundColor: "transparent", mixBlendMode: "normal" },
          spotlight: { backgroundColor: "transparent", borderRadius: layoutTokens.radius.lg, border: `8px solid ${semanticColors["surface/card"]}`, boxSizing: "border-box", boxShadow: `0 0 0 9999px ${semanticColors["surface/overlay"]}` },
          spotlightLegacy: { borderRadius: layoutTokens.radius.lg, border: `8px solid ${semanticColors["surface/card"]}`, boxSizing: "border-box" },
        }}
        floaterProps={{
          styles: { arrow: { length: 8, spread: 16 } },
          options: {
            // Popper 1のviewportはhtmlの文書全体の高さになるため、実際の表示領域で判定する。
            viewportBoundary: {
              enabled: true,
              order: 250,
              fn: (data: PopperData) => {
                const instance = data.instance as PopperData["instance"] & { modifiers: { name: string; boundariesElement?: Element }[] };
                const boundary = viewportBoundary.current;
                if (boundary) {
                  instance.modifiers.filter(modifier => modifier.name === "preventOverflow" || modifier.name === "flip")
                    .forEach(modifier => { modifier.boundariesElement = boundary; });
                }
                return data;
              },
            },
            preventOverflow: { padding: layoutTokens.space[16] },
            flip: { enabled: true, behavior: "flip", padding: layoutTokens.space[16] },
          },
        }}
      />
    </>
  );
};
